// src/services/works/koreanLabelFields.js
//
// 한글 표시사항 칸 id 정본 — 이미지 생성(koreanLabelService.generateLabelImage 의 labelNameMap)과 같다.
//   manufacturer = 제조원, importer = 수입 및 판매원
// 예전 편집 화면(KoreanLabelEdit.vue)은 manufacturer = «수입 및 판매원», origin = «제조원» 으로 저장했다.
// 그 꼴로 저장된 데이터는 읽을 때만 정본 칸으로 옮긴다(DB 는 고치지 않는다 — 다시 저장하면 정본 꼴로 남는다).
// 예전 꼴 판정 (둘 중 하나):
//   ① 저장된 구조(koreanLabelStructure)의 manufacturer 줄 이름표가 «수입 및 판매원» — 사용자가 제조원(origin) 줄을 지운 경우도 잡는다
//   ② 'origin' 칸이 있고 'importer' 칸이 없다 (생성 쪽은 origin 을 쓰지 않는다)

const LEGACY_IMPORTER_LABEL = '수입 및 판매원'

function isLegacy(keys, structure) {
  if (keys.includes('importer')) return false
  if (Array.isArray(structure) && !structure.some(s => s && s.id === 'importer')
    && structure.some(s => s && s.id === 'manufacturer' && s.label === LEGACY_IMPORTER_LABEL)) return true
  return keys.includes('origin')
}

// koreanLabels 객체 { manufacturer, origin, ... } → { importer, manufacturer, ... }
// structure: 같은 상품에 저장된 koreanLabelStructure (있으면 판정 ① 에 쓴다)
export function migrateLegacyLabels(labels, structure) {
  if (!labels || typeof labels !== 'object') return labels
  if (!isLegacy(Object.keys(labels), structure)) return labels
  const out = {}
  for (const key of Object.keys(labels)) {
    if (key === 'manufacturer') out.importer = labels.manufacturer
    else if (key === 'origin') out.manufacturer = labels.origin
    else out[key] = labels[key]
  }
  return out
}

// koreanLabelStructure 배열 [{ id, label, required }] — 칸 순서·사용자가 고친 이름표는 그대로, id 만 옮긴다.
export function migrateLegacyStructure(structure) {
  if (!Array.isArray(structure)) return structure
  if (!isLegacy(structure.map(s => s && s.id), structure)) return structure
  return structure.map(s => {
    if (s && s.id === 'manufacturer') return { ...s, id: 'importer' }
    if (s && s.id === 'origin') return { ...s, id: 'manufacturer' }
    return s
  })
}

// 내용량은 «몇 개(또는 얼마의 양)가 들었나» 다. AI 가 상품 속성의 «크기 52*20*0.8mm» 를 그대로 옮겨
// 내용량을 «52*20*0.8mm (중형)» 으로 적은 사례(PR261016-8A4E47, daycozy01)가 있어 코드로 한 번 더 본다.
// 세는 단위(개·매·세트…)나 양 단위(ml·g·kg…)가 숫자 뒤에 하나도 없으면 묶음 수량(기본 1개)을 덧붙인다.
const COUNT_UNIT = /\d\s*(?:개입|개|매|쌍|세트|팩|장|켤레|병|봉|박스|캔|포|족|권|롤|자루|입|정|알|구|매입|pcs|ea|EA|P|p)(?![a-zA-Z])/
const AMOUNT_UNIT = /\d\s*(?:ml|mL|ML|l|L|g|kg|KG|Kg|mg|oz)(?![a-zA-Z])/

export function hasContentQuantity(content) {
  const s = String(content || '')
  return COUNT_UNIT.test(s) || AMOUNT_UNIT.test(s)
}

// content: 내용량(묶음 반영 뒤) · bundleQuantity: 옵션 묶음 수량(없으면 1)
export function ensureContentQuantity(content, bundleQuantity) {
  const quantity = Number(bundleQuantity) > 0 ? Number(bundleQuantity) : 1
  const base = String(content || '').trim()
  if (hasContentQuantity(base)) return base
  if (!base || base === '상세정보참조') {
    console.warn(`한글표시사항 내용량: 값이 없어(${base || '빈 값'}) ${quantity}개 로 바꾼다`)
    return `${quantity}개`
  }
  console.warn(`한글표시사항 내용량: 수량 단위가 없어 ${quantity}개 를 덧붙인다 - ${base}`)
  return `${base} / ${quantity}개`
}

// 표시사항 AI 지침 중 «근거» 규칙 — koreanLabelService.promptForAllOptionLabels 가 싣는다(시험하려고 순수 파일에 둔다).
// 예전 지침(«Color-related options might have different materials»·«Generate realistic and appropriate values»)이
// 근거 없는 재질을 지어내게 했다(2026-10-20 201B76 daycozy01 · 563900·5CB8D6). 근거가 없으면 «상세정보참조».
export const LABEL_EVIDENCE_RULES = [
  '- Use ONLY facts stated in the Product Name, Product Attributes, or the option name itself (e.g. a size like "12cm"). Do NOT guess or invent materials, ingredients, or contents',
  '- If the material/ingredient or content is not stated there, write exactly "상세정보참조" — never fill it with a plausible-sounding value (추정·지어냄 금지)',
  '- Do not assume options differ in material just because their colors differ',
].join('\n')
